import {useState} from "react";
import {Box, Chip, ChipType, Colors, Container, Link, Text} from "@bounce/shared/ui";
import {Tooltip} from "../ui/components";

export type ResourceListItem = {
  id: string;
  label: string;
  imageUrl?: string;
  tooltip?: string;
  variant?: ChipType["variant_"];
  onClick?: () => void;
};

type Props = {
  items: ResourceListItem[];
  maxVisible?: number;
};

const THUMBNAIL_SIZE = 44;
const EXPANDED_LIMIT = 50;
const EXPANDED_MAX_HEIGHT = 220;

const ResourceList = ({items, maxVisible = 5}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = items.length - maxVisible;
  const visible =
    hiddenCount <= 0 ? items : expanded ? items.slice(0, EXPANDED_LIMIT) : items.slice(0, maxVisible);
  const notShown = items.length - visible.length;

  return (
    <Container fullWidth sx={{gap: 0.5, alignItems: "flex-start"}}>
      <Container
        row
        fullWidth
        sx={{
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-start",
          alignItems: "center",
          ...(expanded && {maxHeight: EXPANDED_MAX_HEIGHT, overflowY: "auto", pr: 0.5}),
        }}
      >
      {visible.map((item) =>
        item.imageUrl ? (
          <Tooltip key={item.id} title={item.tooltip ?? item.label}>
            <Box
              component="img"
              src={item.imageUrl}
              alt={item.label}
              onClick={item.onClick}
              sx={{
                width: THUMBNAIL_SIZE,
                height: THUMBNAIL_SIZE,
                objectFit: "contain",
                backgroundColor: Colors.bw.white,
                border: `1px solid ${Colors.grey._200}`,
                borderRadius: "8px",
                p: 0.5,
                cursor: item.onClick ? "pointer" : "default",
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip key={item.id} title={item.tooltip ?? ""}>
            <Chip
              size_="small"
              variant_={item.variant ?? "inherit"}
              label={item.label}
              onClick={item.onClick}
              sx={item.onClick ? {cursor: "pointer"} : undefined}
            />
          </Tooltip>
        )
      )}
        {hiddenCount > 0 && !expanded && (
          <Link onClick={() => setExpanded(true)} sx={{cursor: "pointer", fontSize: 13, ml: 0.5}}>
            +{hiddenCount} more
          </Link>
        )}
      </Container>

      {expanded && (
        <Container row sx={{gap: 1.5, alignItems: "center", justifyContent: "flex-start"}}>
          <Link onClick={() => setExpanded(false)} sx={{cursor: "pointer", fontSize: 13}}>
            Show less
          </Link>
          {notShown > 0 && (
            <Text size={12} variant="inter400" sx={{color: Colors.grey._600}}>
              showing {visible.length} of {items.length}
            </Text>
          )}
        </Container>
      )}
    </Container>
  );
};

export default ResourceList;
